import { PlatformCommittee } from '../../../types/Committee'
import { CommitteeTag } from '../../../types/API'
import { EntityTransformer, ValidationResult } from './types'
import { fuzzyMatch } from '../../fuzzyMatch'
import { CommitteeTagCandidates } from '../index'

export interface CommitteeFormData {
  name?: string
  tags?: CommitteeTag[]
  individuals?: { items: unknown[] }
  pendingMembers?: string[]
  existingIndividualIds?: string[]
}

/**
 * Parse a comma-separated string into an array of trimmed values
 */
function parseCommaSeparatedList (value: unknown): string[] {
  if (typeof value !== 'string') {
    return []
  }
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0)
}

/**
 * Infer committee type from the committee name using fuzzy matching
 */
function inferCommitteeType (name: string): CommitteeTag {
  const inferredType = fuzzyMatch(name, CommitteeTagCandidates)
  return inferredType ?? CommitteeTag.Other
}

export const committeeTransformer: EntityTransformer<PlatformCommittee, CommitteeFormData> = {
  entityType: 'Committee',
  toEntity (raw: Record<string, unknown>): Partial<PlatformCommittee> {
    const name = raw.name as string | undefined

    let committeeType: CommitteeTag | undefined
    if (raw.committeeType) {
      committeeType = raw.committeeType as CommitteeTag
    } else if (raw.type) {
      committeeType = fuzzyMatch(String(raw.type), CommitteeTagCandidates) ?? CommitteeTag.Other
    } else if (name) {
      committeeType = inferCommitteeType(name)
    }

    let members: string[] | undefined
    if (Array.isArray(raw.members)) {
      members = raw.members as string[]
    } else if (raw.members) {
      members = parseCommaSeparatedList(raw.members)
    }

    const tags = committeeType ? [committeeType] : undefined

    const committee: Record<string, unknown> = {
      name,
      tags,
      type: tags?.[0]
    }

    // Add members for preview display (will be resolved to individual IDs on import)
    if (members && members.length > 0) {
      committee.members = members
    }

    return committee as Partial<PlatformCommittee>
  },
  toFormData (entity: Partial<PlatformCommittee>): CommitteeFormData {
    // Entity may have 'members' from import (added in toEntity)
    const entityWithMembers = entity as Partial<PlatformCommittee> & { members?: string[] }

    return {
      name: entity.name,
      tags: entity.tags ?? [],
      individuals: { items: [] },
      pendingMembers: entityWithMembers.members
    }
  },
  fromFormData (formData: CommitteeFormData): Record<string, unknown> {
    const committeeType = formData.tags?.[0] ?? null
    return {
      name: formData.name,
      committeeType,
      members: formData.pendingMembers,
      existingIndividualIds: formData.existingIndividualIds
    }
  },
  validate (entity: Partial<PlatformCommittee>): ValidationResult {
    const missingFields: string[] = []

    if (!entity.name) {
      missingFields.push('Committee Name')
    }

    return {
      valid: missingFields.length === 0,
      missingFields
    }
  }
}
